import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { BillingConfigService } from '@cosmos/angular/api';
import { StateService } from '../services/state.service';
import { EntitlementService } from '../services/entitlement.service';
import { ModuleKey } from '@cosmos/shared';

@Injectable({
  providedIn: 'root'
})
export class ModuleAccessGuard {

  constructor(
    private router: Router,
    private billingConfigService: BillingConfigService,
    private stateService: StateService,
    private entitlementService: EntitlementService,
  ) { }

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot)
    : Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    
    const moduleName = route.data['module'] as ModuleKey;
    
    if (!moduleName) {
      console.warn('ModuleAccessGuard: No module specified in route data');
      return true;
    }

    // Get project ID from route parameters (traverse up to find :project param)
    const projectId = this.getProjectIdFromRoute(route);
    
    if (!projectId) {
      console.warn('ModuleAccessGuard: No project ID found in route');
      this.navigateToProjects();
      return false;
    }

    // Check if we already have entitlements in state for the current project
    const currentProject = this.stateService.getCurrentProject();
    const currentAccess = this.stateService.getModuleAccess();

    if (currentProject?._id === projectId && currentAccess) {
      return this.checkAccess(moduleName, projectId);
    }

    // Otherwise, fetch them
    return this.billingConfigService.getModuleAccess(projectId).pipe(
      tap(access => this.stateService.setModuleAccess(access)),
      map(() => this.checkAccess(moduleName, projectId)),
      catchError(error => {
        console.error('ModuleAccessGuard: Error fetching module access', error);
        this.navigateToProjects();
        return of(false);
      })
    );
  }

  private checkAccess(moduleName: ModuleKey, projectId: string): boolean {
    const hasAccess = this.entitlementService.hasAccess(moduleName);

    if (!hasAccess) {
      console.warn(`ModuleAccessGuard: Access denied for module ${moduleName}`);
      this.navigateToProjectDashboard(projectId);
      return false;
    }

    return true;
  }

  /**
   * Traverses the route tree to find the :project parameter
   */
  private getProjectIdFromRoute(route: ActivatedRouteSnapshot): string | null {
    let current: ActivatedRouteSnapshot | null = route;
    
    while (current) {
      if (current.params['project']) {
        return current.params['project'];
      }
      current = current.parent;
    }
    
    return null;
  }

  private navigateToProjects(): void {
    this.router.navigate(['/projects']);
  }

  private navigateToProjectDashboard(projectId: string): void {
    this.router.navigate(['/projects', projectId]);
  }
}
